// Topics on the lane axis: the session's cluster journey, projected onto one
// trace's turn sequence.
//
// The lanes axis is TURN SEQUENCE; the journey is a sequence of PROMPT EVENTS
// (one per request the session issued, each carrying the cluster the pipeline
// assigned it). The two count the same session by different machinery, so the
// only join available is TIME: the axis' approximate per-turn clock
// (traceTurnTimes) dates every turn, and a prompt lands on the first turn whose
// dated completion is at or after it — the rule the system band already buckets
// OS telemetry with. Everything here inherits that approximation.
//
// Two properties make the band a reading rather than a scatter of dots:
//
//   CARRY   a turn with no prompt of its own takes the previous turn's topic.
//           The question is "what was this session working on while this turn
//           happened", and the answer does not go blank between two requests.
//   BLANK   turns before the clock's first dated instant carry nothing. A
//           conversation whose anchors ran out has an undated head, and a blank
//           stretch (no evidence for the join) must not read as a topic.
//
// Turn-weighted on purpose: a bucket's mix is how many of its TURNS sat in each
// topic, not how many requests landed there. The axis' unit is the turn, and a
// per-request weighting would let one busy instant outvote a long stretch.
import { CLUSTER_HUES } from "./traceAnatomy.js";

// Clusters outside the session's top few share one muted paint: the band names
// them on hover, but the eye should only have to track the topics that carry
// the session.
export const TOPIC_OTHER_HUE = "var(--muted-soft)";

/**
 * Topics by how much of THIS axis they hold. Not the session's ranking: a topic
 * that dominates the session can be one turn of this conversation, and the one
 * that dominates this conversation can be a footnote of the session.
 *
 * @returns [{ id, turns }] descending
 */
export function rankTopics(topicOf = []) {
  const tally = new Map();
  for (const id of topicOf) if (id != null) tally.set(id, (tally.get(id) || 0) + 1);
  return [...tally.entries()]
    .map(([id, turns]) => ({ id, turns }))
    .sort((a, b) => b.turns - a.turns);
}

/**
 * Hue per cluster: this axis' leaders get the palette, in their order.
 *
 * The journey band colours the SESSION's eight biggest topics in session-volume
 * order, and a topic keeps that colour here whenever it can — a reader flipping
 * between the two readings should not have to relearn the palette. But rank on
 * THIS axis wins the tie, because session rank alone leaves the conversation's
 * own dominant topic grey (it is frequently outside the session's top eight,
 * which is precisely the case the band exists to show).
 *
 * So the palette goes to this axis' biggest topics, and among those, journey
 * hues are handed out FIRST — a newcomer takes a colour the journey band left
 * unspent rather than displacing one and cascading a rename down the rest.
 * Past the palette, topics are muted: the band names them on hover, but the eye
 * only tracks what carries the conversation.
 *
 * @param topClusters buildJourney().topClusters — the session's ranking
 * @param ranked      rankTopics(axis.topicOf) — this axis' ranking
 */
export function topicHues(topClusters = [], ranked = []) {
  const sessionRank = new Map((topClusters || []).map((c, i) => [c.id, i]));
  const candidates = (ranked || []).slice(0, CLUSTER_HUES.length).map((t) => t.id);
  const out = new Map();
  const used = new Set();
  for (const id of candidates) {
    const rank = sessionRank.get(id);
    const preferred = rank == null ? null : CLUSTER_HUES[rank % CLUSTER_HUES.length];
    if (preferred && !used.has(preferred)) {
      out.set(id, preferred);
      used.add(preferred);
    }
  }
  for (const id of candidates) {
    if (out.has(id)) continue;
    const hue = CLUSTER_HUES.find((h) => !used.has(h));
    if (!hue) break;
    out.set(id, hue);
    used.add(hue);
  }
  return out;
}

/**
 * Re-date the clock's undated HEAD against a floor it does not have.
 *
 * buildTurnTimes clamps every turn before its first paired anchor to that
 * anchor's instant — it refuses to invent earlier times. That is right for
 * bucketing OS telemetry, where a wrong instant files an event under the wrong
 * turn, but it costs the topic band the head of every long conversation: reply
 * anchors are capped, so a thousand-turn axis is measured only over its tail
 * and the band would go blank across the majority of the work.
 *
 * The journey carries a floor the clock does not: the session's earliest
 * observed request. The head is spread evenly between that floor and the first
 * measured anchor. There is no evidence INSIDE the head, so this is an estimate
 * and the caller is handed the boundary to mark it with.
 *
 * @returns { times, estimatedBefore } — the first turn that is NOT estimated
 *          (0 when nothing was re-dated)
 */
export function extendHead(times, floorMs) {
  const n = Array.isArray(times) ? times.length : 0;
  if (!n) return { times, estimatedBefore: 0 };
  let head = 1;
  while (head < n && times[head] === times[0]) head += 1;
  // Nothing clamped, no floor, or a floor that is not actually earlier: the
  // measured clock already says everything there is to say.
  if (head < 2 || !Number.isFinite(floorMs) || !(floorMs < times[0])) {
    return { times, estimatedBefore: 0 };
  }
  const out = times.slice();
  const ceil = times[head - 1];
  for (let i = 0; i < head - 1; i += 1) out[i] = floorMs + ((ceil - floorMs) * i) / (head - 1);
  return { times: out, estimatedBefore: head - 1 };
}

/**
 * Project the journey onto the turn axis.
 *
 * @param rows  journey rows [{ tsMs, clusterId, clusterName }], any order
 * @param times buildTurnTimes().times — approximate completion per turn,
 *              monotone non-decreasing
 * @returns null when nothing can be placed, else
 *   {
 *     topicOf,       cluster id per turn (null = no evidence yet)
 *     counts,        per-turn Map(clusterId -> prompts) | null
 *     measured,      prompts dated to each turn
 *     names,         Map(clusterId -> cluster name)
 *     undatedHead,   turns at the head the clock could not separate
 *     before,        prompts earlier than the axis' first dated instant
 *     after,         prompts past its last, folded onto the final turn
 *     turnsWithTopic, turnsMeasured
 *   }
 */
export function buildTopicAxis(rows, times) {
  const n = Array.isArray(times) ? times.length : 0;
  const seq = (rows || []).filter((r) => r && r.clusterId != null && Number.isFinite(r.tsMs));
  if (!n || !seq.length) return null;
  // The head clamp: buildTurnTimes gives every turn before its first paired
  // reply that reply's time, so a leading run of equal times is the stretch the
  // clock cannot separate — not a stretch where everything happened at once.
  let undatedHead = 1;
  while (undatedHead < n && times[undatedHead] === times[0]) undatedHead += 1;
  if (undatedHead >= n) return null; // one instant for the whole axis dates nothing
  const floor = times[undatedHead - 1];

  const sorted = [...seq].sort((a, b) => a.tsMs - b.tsMs);
  const counts = new Array(n).fill(null);
  const measured = new Array(n).fill(0);
  const names = new Map();
  let before = 0;
  let after = 0;
  // One walk, two ascending sequences: `t` never rewinds.
  let t = undatedHead;
  for (const r of sorted) {
    if (r.clusterName && !names.has(r.clusterId)) names.set(r.clusterId, r.clusterName);
    // Strictly earlier is history the axis cannot place; a request AT the floor
    // is the instant the axis starts from, and belongs to its first dated turn.
    if (r.tsMs < floor) { before += 1; continue; }
    while (t < n - 1 && times[t] < r.tsMs) t += 1;
    if (r.tsMs > times[n - 1]) after += 1;
    const m = counts[t] || (counts[t] = new Map());
    m.set(r.clusterId, (m.get(r.clusterId) || 0) + 1);
    measured[t] += 1;
  }

  const topicOf = new Array(n).fill(null);
  let carry = null;
  let turnsWithTopic = 0;
  let turnsMeasured = 0;
  for (let i = undatedHead; i < n; i += 1) {
    const m = counts[i];
    if (m) {
      // First-seen wins a tie, same rule the journey band's bins use.
      let best = null;
      let bestN = 0;
      for (const [id, c] of m) if (c > bestN) { bestN = c; best = id; }
      carry = best;
      turnsMeasured += 1;
    }
    topicOf[i] = carry;
    if (carry != null) turnsWithTopic += 1;
  }
  if (!turnsWithTopic) return null;
  return { topicOf, counts, measured, names, undatedHead, before, after, turnsWithTopic, turnsMeasured };
}

/**
 * Maximal spans of one topic. A blank turn ends a run rather than bridging it.
 *
 * @returns [{ clusterId, from, to }] both ends inclusive
 */
export function topicRuns(topicOf = []) {
  const runs = [];
  for (let i = 0; i < topicOf.length; i += 1) {
    const id = topicOf[i];
    if (id == null) continue;
    const last = runs[runs.length - 1];
    if (last && last.clusterId === id && last.to === i - 1) last.to = i;
    else runs.push({ clusterId: id, from: i, to: i });
  }
  return runs;
}

/**
 * The band's drawable columns at one zoom: per turn while a turn column can
 * hold a segment, merged when it cannot — the same detail-for-density trade the
 * lane marks and density bars make. A merged column carries its whole MIX, so
 * compressing the axis costs topic detail but never coverage.
 *
 * @returns [{ start, end, turns, mix: [{ clusterId, turns }] desc, top, unknown }]
 *          `mix` puts the hued topics first, in share order, and collapses
 *          everything muted into ONE trailing entry (clusterId null): a stack of
 *          six identical grey slivers is not six readings, and the named topics
 *          need the contiguous room.
 */
export function topicBuckets(topicOf = [], cellW = 1, minPx = 3, hues = null) {
  const n = topicOf.length;
  const span = cellW >= minPx ? 1 : Math.max(1, Math.ceil(minPx / cellW));
  const out = [];
  for (let start = 0; start < n; start += span) {
    const end = Math.min(n - 1, start + span - 1);
    const tally = new Map();
    let unknown = 0;
    for (let i = start; i <= end; i += 1) {
      const id = topicOf[i];
      if (id == null) { unknown += 1; continue; }
      tally.set(id, (tally.get(id) || 0) + 1);
    }
    const ranked = [...tally.entries()]
      .map(([clusterId, turns]) => ({ clusterId, turns }))
      .sort((a, b) => b.turns - a.turns);
    const top = ranked[0]?.clusterId ?? null;
    let mix = ranked;
    if (hues) {
      const named = ranked.filter((e) => hues.has(e.clusterId));
      const rest = ranked.filter((e) => !hues.has(e.clusterId));
      const restTurns = rest.reduce((a, e) => a + e.turns, 0);
      // The collapsed entry keeps its members' ids, share-ranked: a column whose
      // whole mix is muted still has to be able to name what it holds.
      mix = restTurns
        ? [...named, { clusterId: null, turns: restTurns, others: rest.length, ids: rest.map((e) => e.clusterId) }]
        : named;
    }
    out.push({ start, end, turns: end - start + 1, mix, top, unknown });
  }
  return out;
}
